import type {
  DashboardPageStreamEvent,
  PageDecisionDigest,
} from './global-ai-run-page-stream';
import type { ToolProgressEvent } from './global-ai-tool-progress';
import type { ChartDataDigest } from './turn-outcome-digest';

/**
 * A structured generation step event, decoupled from how it is spoken. Derived
 * FE-side from the page-stream today; `generatePage` is the long-term producer.
 */
export type PageGenProgressEvent =
  | {
      phase:
        | 'thinking'
        | 'planning'
        | 'building'
        | 'filtering'
        | 'verifying'
        | 'revising';
      /** Optional human label; the FE falls back to a phase default. */
      label?: string;
    }
  | {
      phase: 'finding';
      digest: ChartDataDigest | PageDecisionDigest;
      note?: string;
    };

const PHASE_DEFAULT_LABEL: Record<
  Exclude<PageGenProgressEvent['phase'], 'finding'>,
  string
> = {
  thinking: 'Thinking…',
  planning: 'Designing the layout…',
  building: 'Building the charts…',
  filtering: 'Applying filters…',
  verifying: 'Reviewing the results…',
  revising: 'Refining the dashboard…',
};

/**
 * Derive a progress event from a page-stream event. Returns null for events the
 * bridge handles separately (config/filters/questions/error).
 */
export function dashboardStreamEventToProgress(
  ev: DashboardPageStreamEvent
): PageGenProgressEvent | null {
  switch (ev.type) {
    case 'thinking':
      return { phase: 'thinking', label: ev.content };
    case 'status':
      return { phase: 'building', label: ev.content };
    case 'intent':
      return { phase: 'planning' };
    case 'verifying':
      return { phase: 'verifying', label: ev.content };
    case 'finding':
      return { phase: 'finding', digest: ev.content };
    default:
      return null;
  }
}

/**
 * Decide voice for a progress event: `thinking` streams the model's live
 * reasoning verbatim into the thinking bubble; a `finding` routes to one
 * grounded line via narrate-progress; the rest are activity-lane status (no LLM).
 */
export function routePageGenProgress(
  e: PageGenProgressEvent
): ToolProgressEvent {
  if (e.phase === 'finding') {
    return { tier: 'narrate', kind: 'page_finding', digest: e.digest };
  }
  if (e.phase === 'thinking') {
    return { tier: 'thinking', textChunk: e.label ?? '' };
  }
  return { tier: 'status', text: e.label ?? PHASE_DEFAULT_LABEL[e.phase] };
}
